import React, { useState } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import Button from '../../components/common/Button';
import { ExerciseTargetDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import FileUpload from '../common/FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import { ValidationRules, useFormValidation } from '../../hooks/useFormValidation';
import ImageLink from '../common/ImageLink';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface ExerciseTargetFormProps {
  initialValues: ExerciseTargetDTO;
  onSubmit: (data: ExerciseTargetDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  targetImages: {
    id: 'target-images',
    title: 'Target Images',
  },
  evaluationImages: {
    id: 'evaluation-images',
    title: 'Evaluation Images',
  },
  competitionImages: {
    id: 'competition-images',
    title: 'Competition Images',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

const ExerciseTargetForm: React.FC<ExerciseTargetFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ExerciseTargetDTO>(initialValues);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [displayImageUrl, setDisplayImageUrl] = useState<string>('');

  const { uploadFile, getFileUrl } = useFileApi();

  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    code: [{ required: true, message: 'Code is required' }],
    description: [{ required: true, message: 'Description is required' }],
  };

  const { errors, validate } =
    useFormValidation<ExerciseTargetDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleFileChange = (field: keyof ExerciseTargetDTO) =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImageFile(file);
        try {
          setUploadingFiles((prev) => ({ ...prev, [field]: true }));
          const response = await uploadFile('files', file.name, file, 'targets');
          if (response && response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              [field]: response.data
            }));
          }
        } catch (error) {
          console.error('Error handling file:', error);
        } finally {
          setUploadingFiles((prev) => ({ ...prev, [field]: false }));
        }
      }
    };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
        <FormField
          id="code"
          label="Code"
          type="text"
          value={formData.code || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Code"
          required
          error={errors.code}
        />
        <FormField
          id="description"
          label="Description"
          type="text"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Description"
          required
          error={errors.description}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.targetImages.id}
        title={FORM_SECTIONS.targetImages.title}
      >
        {formData.targetImageId && (
          <ImageLink
            fileId={formData.targetImageId}
            label="Target Image"
          />
        )}
        <FileUpload
          id="imageUpload"
          onChange={handleFileChange('targetImageId')}
          title="Upload Target Image File"
          accept={FileTypeConstants.ALL_SUPPORTED}
          isUploading={uploadingFiles['targetImageId']}
        />

        {formData.targetImageBwId && (
          <ImageLink
            fileId={formData.targetImageBwId}
            label="Target Image (BW)"
          />
        )}
        <FileUpload
          id="imageBwUpload"
          onChange={handleFileChange('targetImageBwId')}
          title="Upload Target Image (BW) File"
          accept={FileTypeConstants.ALL_SUPPORTED}
          isUploading={uploadingFiles['targetImageBwId']}
        />
        <FormField
          id="rulesUrl"
          label="Target Rules (URL)"
          type="text"
          value={formData.rulesUrl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Rules (URL)"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.evaluationImages.id}
        title={FORM_SECTIONS.evaluationImages.title}
      >
        {formData.evaluationSportImageId && (
          <ImageLink
            fileId={formData.evaluationSportImageId}
            label="Target Evaluation (Sport)"
          />
        )}
        <FileUpload
          id="evaluationSportImageUpload"
          onChange={handleFileChange('evaluationSportImageId')}
          title="Upload Target Evaluation (Sport) File"
          accept={FileTypeConstants.ALL_SUPPORTED}
          isUploading={uploadingFiles['evaluationSportImageId']}
        />

        {formData.evaluationHuntImageId && (
          <ImageLink
            fileId={formData.evaluationHuntImageId}
            label="Target Evaluation (Hunt)"
          />
        )}
        <FileUpload
          id="evaluationHuntImageUpload"
          onChange={handleFileChange('evaluationHuntImageId')}
          title="Upload Target Evaluation (Hunt) File"
          accept={FileTypeConstants.ALL_SUPPORTED}
          isUploading={uploadingFiles['evaluationHuntImageId']}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.competitionImages.id}
        title={FORM_SECTIONS.competitionImages.title}
      >
        {formData.competitionImageId && (
          <ImageLink
            fileId={formData.competitionImageId}
            label="Target Competition"
          />
        )}
        <FileUpload
          id="competitionImageUpload"
          onChange={handleFileChange('competitionImageId')}
          title="Upload Target Competition File"
          accept={FileTypeConstants.ALL_SUPPORTED}
          isUploading={uploadingFiles['competitionImageId']}
        />
        <FormField
          id="competitionRulesUrl"
          label="Competition Rules (URL)"
          type="text"
          value={formData.competitionRulesUrl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Competition Rules (URL)"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="isPortrait"
          label="Portrait?"
          type="checkbox"
          value={formData.isPortrait || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make document portrait"
        />
        <FormField
          id="isActive"
          label="Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active"
        />
      </FormSection>
    </Form>
  );
};

export default ExerciseTargetForm;
